/**
 * Manual labour entry — "I forgot to start the timer." A start/end span with
 * one optional break; the server carves it into closed segments and stamps
 * them with the task id, so the time lands in the SAME per-task total as the
 * live start/pause timer. Times are entered in shop-local time and sent as
 * ISO-8601 instants.
 */
import { useEffect, useState } from 'react'
import { Button, Modal } from '@vollgas/shared/ui'
import type { ManualLabourRequest } from '@/apis/types'
import { useI18n } from '@/i18n/TranslationContext'
import { useAddTaskManualLabour } from '@/hooks/useWorkshop'

interface Props {
  readonly open: boolean
  readonly serviceId: string
  readonly onClose: () => void
}

/** `datetime-local` value ("2026-06-01T09:00") → ISO instant, or null. */
function toIso(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

export function ManualLabourModal({ open, serviceId, onClose }: Props) {
  const { t } = useI18n()
  const add = useAddTaskManualLabour()
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [withBreak, setWithBreak] = useState(false)
  const [breakStart, setBreakStart] = useState('')
  const [breakEnd, setBreakEnd] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setStart(''); setEnd(''); setWithBreak(false)
      setBreakStart(''); setBreakEnd(''); setError(null)
    }
  }, [open])

  const submit = () => {
    setError(null)
    const startedAt = toIso(start)
    const endedAt = toIso(end)
    if (!startedAt || !endedAt) { setError(t.manualLabour.errorSpan); return }
    if (new Date(endedAt) <= new Date(startedAt)) { setError(t.manualLabour.errorOrder); return }

    const body: ManualLabourRequest = { startedAt, endedAt }
    if (withBreak) {
      const ps = toIso(breakStart)
      const pe = toIso(breakEnd)
      if (!ps || !pe || new Date(pe) <= new Date(ps)) { setError(t.manualLabour.errorBreak); return }
      ;(body as { pauses?: ManualLabourRequest['pauses'] }).pauses = [{ startedAt: ps, endedAt: pe }]
    }

    add.mutate({ serviceId, body }, {
      onSuccess: onClose,
      onError: () => setError(t.manualLabour.errorSubmit),
    })
  }

  const FIELD = 'w-full rounded-sm border border-glass-border bg-glass-fill px-3 py-2 text-13 text-glass-text focus:border-accent focus:outline-none'

  return (
    <Modal open={open} onClose={onClose} closeLabel={t.common.close} labelledById="manual-labour-title" size="md">
      <h2 id="manual-labour-title" className="mb-1 text-lg font-extrabold text-glass-text">{t.manualLabour.title}</h2>
      <p className="mb-3 text-13 text-glass-text-muted">{t.manualLabour.hint}</p>

      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-12-5 font-semibold text-glass-text-muted">
          {t.manualLabour.start}
          <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className={FIELD} />
        </label>
        <label className="flex flex-col gap-1 text-12-5 font-semibold text-glass-text-muted">
          {t.manualLabour.end}
          <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className={FIELD} />
        </label>

        <label className="flex items-center gap-2 text-13 text-glass-text">
          <input type="checkbox" checked={withBreak} onChange={(e) => setWithBreak(e.target.checked)} />
          {t.manualLabour.addBreak}
        </label>
        {withBreak && (
          <div className="flex flex-col gap-3 rounded-sm border border-dashed border-glass-border p-3">
            <label className="flex flex-col gap-1 text-12-5 font-semibold text-glass-text-muted">
              {t.manualLabour.breakStart}
              <input type="datetime-local" value={breakStart} onChange={(e) => setBreakStart(e.target.value)} className={FIELD} />
            </label>
            <label className="flex flex-col gap-1 text-12-5 font-semibold text-glass-text-muted">
              {t.manualLabour.breakEnd}
              <input type="datetime-local" value={breakEnd} onChange={(e) => setBreakEnd(e.target.value)} className={FIELD} />
            </label>
          </div>
        )}
      </div>

      {error && <p role="alert" className="mt-2 text-13 text-accent">{error}</p>}

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-sm border border-glass-border bg-transparent px-4 py-px11 text-13 font-semibold text-glass-text-muted hover:bg-glass-fill-strong hover:text-glass-text"
        >
          {t.common.cancel}
        </button>
        <Button variant="primary" size="lg" onClick={submit} loading={add.isPending} className="flex-1">
          {t.manualLabour.submit}
        </Button>
      </div>
    </Modal>
  )
}
